import { Box, Button, ButtonProps, Tooltip, darken, rem } from '@mantine/core';
import cx from 'clsx';
import { ReactNode, Ref } from 'react';

import classes from './StyledButton.module.scss';
import { isColorsContrastAccessible } from '../../../utils/isColorsContrastAccessible';
import { getContrastColor } from '../../../utils/useGetContrastColor';
import { useGlobalTheme } from '../../../utils/useGlobalTheme';

type StyledButtonProps = {
  children: ReactNode;
  role?: 'main' | 'helper';
  onClick?: () => void;
  className?: string;
  /** Disabled look + blocked click that keeps hover events, so a wrapping tooltip can explain why. */
  dataDisabled?: boolean;
  disabled?: boolean;
  label?: string;
  dataTestId?: string;
  rightSection?: ButtonProps['rightSection'];
  height?: number;
  size?: number;
  preventTransparency?: boolean;
  isLoading?: boolean;
  /** Lets a wrapping Tooltip reach the underlying button element. */
  ref?: Ref<HTMLButtonElement>;
};

function StyledButtonInner(props: StyledButtonProps) {
  const {
    role = 'main',
    children,
    dataDisabled,
    disabled,
    dataTestId,
    onClick,
    className,
    rightSection,
    height,
    preventTransparency,
    label,
    isLoading,
    ref
  } = props;

  const theme = useGlobalTheme();
  const buttonType = theme.other.questions.buttons[role].style;
  const buttonBorderRadius = theme.other.questions.buttons[role].borderRadius;
  const primaryColor = theme.other.colors.system.primary;
  const neutralGrayColor = theme.other.colors.neutral.gray1;
  const whiteColor = theme.other.colors.neutral.white;
  const blackColor = theme.other.colors.neutral.black;

  let backgroundColor;
  let color;
  let hoverBackgroundColor = 'inherit';
  let hoverColor = 'inherit';

  switch (buttonType) {
    case 'primary': {
      backgroundColor = primaryColor;
      color = getContrastColor(theme, backgroundColor);
      hoverBackgroundColor = darken(primaryColor, 0.2);
      hoverColor = getContrastColor(theme, hoverBackgroundColor);
      break;
    }
    case 'secondary': {
      const isSecondaryContrastAccessible = isColorsContrastAccessible(neutralGrayColor, primaryColor);
      backgroundColor = isSecondaryContrastAccessible ? neutralGrayColor : whiteColor;
      color = isSecondaryContrastAccessible ? primaryColor : blackColor;
      break;
    }
    case 'outlined': {
      const isOutlinedContrastAccessible = isColorsContrastAccessible(whiteColor, primaryColor);
      backgroundColor = whiteColor;
      color = isOutlinedContrastAccessible ? primaryColor : blackColor;
      hoverBackgroundColor = primaryColor;
      hoverColor = getContrastColor(theme, hoverBackgroundColor);
      break;
    }
    case 'text': {
      const isTextContrastAccessible = isColorsContrastAccessible(whiteColor, primaryColor);
      backgroundColor = preventTransparency ? (isTextContrastAccessible ? whiteColor : blackColor) : 'transparent';
      color = primaryColor;
      break;
    }
  }

  const padding = role === 'main' ? `${rem(8)} ${rem(24)}` : rem(4);

  return (
    <Button
      ref={ref}
      aria-label={label}
      style={{
        '--background-color': backgroundColor,
        '--color': color,
        '--hover-background-color': hoverBackgroundColor,
        '--hover-color': hoverColor,
        '--borderRadius': buttonBorderRadius.radius,
        '--borderRadiusTopLeft': buttonBorderRadius.topLeft,
        '--borderRadiusTopRight': buttonBorderRadius.topRight,
        '--borderRadiusBottomLeft': buttonBorderRadius.bottomLeft,
        '--borderRadiusBottomRight': buttonBorderRadius.bottomRight,
        '--padding': padding
      }}
      className={cx(classes.button, classes[`${buttonType}Button`], className)}
      aria-disabled={dataDisabled || undefined}
      data-disabled={dataDisabled || undefined}
      disabled={disabled}
      loading={isLoading}
      onClick={dataDisabled ? undefined : onClick}
      h={height ? rem(height) : 'auto'}
      data-testid={dataTestId}
      rightSection={rightSection}
    >
      {children}
    </Button>
  );
}

export function StyledButton(props: StyledButtonProps) {
  const { label, children } = props;

  return label ? (
    <Tooltip label={label}>
      <Box>
        <StyledButtonInner {...props}>{children}</StyledButtonInner>
      </Box>
    </Tooltip>
  ) : (
    <StyledButtonInner {...props}>{children}</StyledButtonInner>
  );
}
